import {
    Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Grid, Typography, makeStyles
} from '@material-ui/core';
import {
    BrokenImage, CheckCircle, Close, ExpandLess, ExpandMore, FileCopy, PhotoLibrary, VerifiedUser,
    Warning
} from '@material-ui/icons';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Toastr from "toastr";
import {
    clearKycDetail, fetchKycDetailRequest, fetchKycRequest, testConnectionRequest
} from '../../store/kyc/kyc.actions';
import KycAPI from '../../store/kyc/kyc.api';
import { getKycApplications, getKycConnection, getKycDetail } from '../../store/kyc/kyc.selector';
import TitleBar from '../base/TitleBar';
import Loader from '../base/loader';

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
    },
    banner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(2),
        borderRadius: 12,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
    },
    bannerDetail: {
        width: '100%',
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(1),
        borderTop: '1px solid rgba(255,255,255,0.08)',
        // FactList is shared with the (light) detail dialog, where Typography's own `color: initial`
        // is what makes it readable. On this dark banner that same rule would paint it black, so the
        // colour is forced from here rather than baked into the shared classes.
        '& .MuiTypography-root': {
            color: 'rgba(255,255,255,0.8)',
        },
    },
    bannerText: {
        color: '#fff',
        fontSize: '0.85rem',
    },
    bannerHint: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: '0.72rem',
        marginTop: 2,
    },
    sectionTitle: {
        fontWeight: 700,
        letterSpacing: 0.4,
    },
    sectionRule: {
        margin: theme.spacing(0.5, 0, 1.5),
    },
    factLabel: {
        fontSize: '0.66rem',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        opacity: 0.6,
    },
    factValue: {
        fontSize: '0.85rem',
        wordBreak: 'break-word',
    },
    payload: {
        margin: theme.spacing(1, 0, 0),
        padding: theme.spacing(1.5),
        maxHeight: 320,
        overflow: 'auto',
        borderRadius: 8,
        background: 'rgba(0,0,0,0.06)',
        fontSize: '0.72rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    tile: {
        position: 'relative',
        height: 150,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        overflow: 'hidden',
        background: 'rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.12)',
    },
    tileImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        cursor: 'zoom-in',
        display: 'block',
    },
    tileCaption: {
        display: 'block',
        marginTop: 4,
        lineHeight: 1.3,
    },
    preview: {
        width: '100%',
        maxHeight: '75vh',
        objectFit: 'contain',
        display: 'block',
    },
}));

const STATUS_COLORS = {
    Approved: '#2e7d32',
    Rejected: '#c62828',
    Pending: '#ef6c00',
    Resubmit: '#1565c0',
    Expired: '#546e7a',
};

const statusChip = (status) => (
    <Chip
        size="small"
        label={status || "—"}
        style={{ backgroundColor: STATUS_COLORS[status] || '#546e7a', color: '#fff' }}
    />
);

// selfie | document_front | document_back | selfie_document | proof_of_address | nfc — shown as the
// provider names them, only spelled for a human.
const artifactLabel = (type) => {
    if (!type) return "Capture";
    const spaced = type.replace(/_/g, " ");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const artifactSize = (size) => (size ? Math.max(1, Math.round(size / 1024)) + " KB" : "");

/** camelCase / snake_case provider keys, spelled for a reader. */
const humanize = (key) => String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const isPlainObject = (value) =>
    !!value && typeof value === 'object' && !Array.isArray(value);

const hasValue = (value) =>
    value !== undefined && value !== null && value !== "" &&
    !(Array.isArray(value) && value.length === 0) &&
    !(isPlainObject(value) && Object.keys(value).length === 0);

/** Booleans read as Yes/No, lists as a sentence; everything else is shown as the provider sent it. */
const displayValue = (value) => {
    if (typeof value === 'boolean') return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
};

const formatDate = (value) => {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("lll") : value;
};

/**
 * The Mixed fields — aadhaar, the two signature verdicts and documentRead — have no fixed shape:
 * the provider adds keys over time, which is exactly why they are stored as Mixed. So they are
 * rendered as they arrive rather than through a field list that would silently drop whatever is new.
 */
function FactList({ value, classes }) {
    return (
        <Grid container spacing={1}>
            {Object.keys(value || {}).map((key) => {
                const entry = value[key];
                if (!hasValue(entry)) return null;
                if (isPlainObject(entry)) {
                    return (
                        <Grid item xs={12} key={key}>
                            <Typography className={classes.factLabel}>{humanize(key)}</Typography>
                            <Box pl={1.5}><FactList value={entry} classes={classes} /></Box>
                        </Grid>
                    );
                }
                return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                        <Typography className={classes.factLabel}>{humanize(key)}</Typography>
                        <Typography className={classes.factValue}>{displayValue(entry)}</Typography>
                    </Grid>
                );
            })}
        </Grid>
    );
}

/**
 * One captured image. The bytes arrive through the service's authenticated proxy — the provider's
 * own path needs the vendor API key and no browser can dereference it — so the tile fetches a blob
 * and renders an object URL, revoking it on unmount. A failure leaves a broken tile on purpose: the
 * service does not distinguish "gone" from "refused", and neither should the console.
 */
function ArtifactTile({ applicationId, artifact, classes, onPreview }) {
    const [src, setSrc] = useState("");
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let objectUrl = "";
        let cancelled = false;
        KycAPI.GetArtifact(applicationId, artifact.id)
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                // Unmounted while in flight — release it here, the cleanup below can no longer see it.
                if (cancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }
                objectUrl = url;
                setSrc(url);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [applicationId, artifact.id]);

    const label = artifactLabel(artifact.type);

    return (
        <Grid item xs={6} sm={4} md={3}>
            <Box className={classes.tile}>
                {src
                    ? <img src={src} alt={label} className={classes.tileImage}
                        onClick={() => onPreview({ src: src, label: label })} />
                    : failed
                        ? <BrokenImage style={{ color: 'rgba(0,0,0,0.35)' }} />
                        : <Typography variant="caption" color="textSecondary">Loading…</Typography>}
            </Box>
            <Typography variant="caption" color="textSecondary" className={classes.tileCaption}>
                <strong>{label}</strong>
                {failed && " — unavailable"}
                {artifact.size ? " · " + artifactSize(artifact.size) : ""}
            </Typography>
        </Grid>
    );
}

export default function KycContainer() {
    const dispatch = useDispatch();
    const classes = useStyles();

    const [detailOpen, setDetailOpen] = useState(false);
    // The stored provider response is long and only occasionally wanted, so it stays folded away
    // until asked for — and re-folds when a different application is opened.
    const [payloadOpen, setPayloadOpen] = useState(false);
    // { src, label } of the capture being viewed full size, or null.
    const [preview, setPreview] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(null);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        dispatch(fetchKycRequest({ page: 0, pageSize: 10 }));
    }, [])

    const {
        applications, detail, connection, isLoading, isDetailLoading, isConnectionLoading, totalApplications
    } = useSelector((state) => {
        return {
            applications: getKycApplications(state),
            detail: getKycDetail(state),
            connection: getKycConnection(state),
            isLoading: state.KycReducer.isLoading,
            isDetailLoading: state.KycReducer.isDetailLoading,
            isConnectionLoading: state.KycReducer.isConnectionLoading,
            totalApplications: state.KycReducer.totalApplications,
        }
    }, []);

    const application = (detail || {}).application || {};
    const member = (detail || {}).member || {};

    const reload = (overrides) => dispatch(fetchKycRequest({
        page: page, pageSize: rowsPerPage, search: searchText, ...overrides
    }));

    const openDetail = (id) => {
        setPreview(null);
        setPayloadOpen(false);
        dispatch(fetchKycDetailRequest(id));
        setDetailOpen(true);
    };

    // The preview renders an object URL owned by a tile inside the detail dialog, and closing that
    // dialog unmounts the tile (which revokes it) — so the two always close together.
    const closeDetail = () => {
        setPreview(null);
        setDetailOpen(false);
        dispatch(clearKycDetail());
    };

    const copy = (value) => {
        navigator.clipboard.writeText(value);
        Toastr.info("copied to clipboard");
    };

    const copyIcon = (value) => (
        <FileCopy color="secondary" fontSize="small" style={{ cursor: 'pointer' }}
            onClick={() => copy(value)} />
    );

    const columns = [
        { name: 'memberId', label: 'Member ID', options: { filter: true, sort: true } },
        { name: 'memberName', label: 'Member', options: { filter: false, sort: true } },
        { name: 'provider', label: 'Provider', options: { filter: true, sort: true } },
        { name: 'method', label: 'Method', options: { filter: true, sort: true } },
        {
            name: 'status',
            label: 'Status',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value) => statusChip(value),
            }
        },
        { name: 'createdAt', label: 'Started', options: { filter: false, sort: true } },
        {
            name: 'id',
            label: 'Action',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => (
                    <VerifiedUser
                        color="secondary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => openDetail(value)}
                    />
                )
            }
        }
    ];

    const datum = (applications || []).map((item) => {
        const listed = item.member && item.member.length > 0 ? item.member[0] : {};
        return {
            id: item._id,
            memberId: item.memberId,
            memberName: [listed.firstName, listed.lastName].filter((n) => !!n).join(" ") || "—",
            provider: item.provider,
            method: item.method || "—",
            status: item.status,
            createdAt: moment(item.createdAt).format("DD-MM-YYYY"),
        };
    });

    const options = {
        selectableRows: false,
        rowsPerPage: rowsPerPage,
        serverSide: true,
        filter: false,
        count: totalApplications,
        onChangeRowsPerPage: (numberOfRows) => {
            setRowsPerPage(numberOfRows);
            reload({ pageSize: numberOfRows });
        },
        onSearchClose: () => {
            setSearchText("");
            reload({ search: "" });
        },
        onTableChange: (action, tableState) => {
            if (action === "changePage") {
                setPage(tableState.page);
                reload({ page: tableState.page });
            } else if (action === "search") {
                setSearchText(tableState.searchText);
                if (tableState.searchText && (tableState.searchText.length > 2 || tableState.searchText.length === 0)) {
                    reload({ search: tableState.searchText });
                }
            }
        }
    };

    /** One field. Absent values are dropped rather than printed as a dash. */
    const fact = (label, value, action) => {
        if (!hasValue(value)) return null;
        return (
            <Grid item xs={12} sm={6} md={4} key={label}>
                <Typography className={classes.factLabel}>{label}</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography className={classes.factValue}>{displayValue(value)}</Typography>
                    {action}
                </Box>
            </Grid>
        );
    };

    const section = (title, body) => (
        <Box mt={2}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>{title}</Typography>
            <Divider className={classes.sectionRule} />
            {body}
        </Box>
    );

    const hasIdentityRecord = hasValue(application.aadhaar) || hasValue(application.aadhaarQr)
        || hasValue(application.aadhaarXml) || hasValue(application.documentRead);

    return (
        <div className='m-2 pb-4'>
            {isLoading && <Loader />}

            {/* Review only. Members start their own verification in the portal and the identity
                provider decides it; this console reads the outcome and its evidence. */}
            <TitleBar heading="KYC Verification" icon={<VerifiedUser />} />

            {/* There is no vendor connection to test from here — this service holds no vendor
                credentials — so the test proves the hop the console actually owns: the signed link
                to the Customer Service, and what that service reports as configured. It runs on the
                button alone, never on page load, because every test is a live call across it. */}
            <Box className={classes.banner}>
                <Box>
                    <Typography className={classes.bannerText}>
                        Identity service&nbsp;
                        {!connection.tested
                            ? <Chip size="small" label="Not tested" style={{ backgroundColor: '#546e7a', color: '#fff' }} />
                            : connection.ok
                                ? <Chip size="small" icon={<CheckCircle style={{ color: '#fff' }} />} label="Connected"
                                    style={{ backgroundColor: '#2e7d32', color: '#fff' }} />
                                : <Chip size="small" icon={<Warning style={{ color: '#fff' }} />} label="Unreachable"
                                    style={{ backgroundColor: '#c62828', color: '#fff' }} />}
                        &nbsp;
                        {connection.service && !connection.service.keyConfigured &&
                            <Chip size="small" label="Access key missing"
                                style={{ backgroundColor: '#ef6c00', color: '#fff' }} />}
                    </Typography>
                    <Typography className={classes.bannerHint}>
                        {connection.tested && !connection.ok
                            ? connection.reason || "The identity service did not answer."
                            : "Verifications are started by members in the portal and decided by the identity provider. This console is read-only."}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {isConnectionLoading && <CircularProgress size={18} />}
                    <Button variant="outlined" size="small" disabled={isConnectionLoading}
                        style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
                        onClick={() => dispatch(testConnectionRequest())}>
                        Test connection
                    </Button>
                </Box>

                {/* Whatever the Customer Service reported back, rendered as it arrived: this is a
                    diagnostic, and the shape of that answer is that service's to change. */}
                {connection.tested && hasValue(connection.provider) &&
                    <Box className={classes.bannerDetail}>
                        <FactList value={connection.provider} classes={classes} />
                    </Box>}
            </Box>

            {/* ── One application, in full ──────────────────────────────────────────────── */}
            <Dialog maxWidth="md" fullWidth open={detailOpen} onClose={() => closeDetail()}>
                <DialogTitle>
                    KYC Application
                    <Close className='mt-1 float-right' style={{ cursor: 'pointer' }} onClick={() => closeDetail()} />
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {isDetailLoading &&
                        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                            <CircularProgress size={28} />
                        </Box>}

                    {!isDetailLoading && application._id &&
                        <>
                            {section("Member",
                                <Grid container spacing={1}>
                                    {fact("Name", [member.firstName, member.lastName].filter((n) => !!n).join(" "))}
                                    {fact("Member ID", application.memberId, copyIcon(application.memberId))}
                                    {fact("Email", member.email, copyIcon(member.email))}
                                    {fact("Mobile", member.mobileNumber)}
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography className={classes.factLabel}>KYC verified</Typography>
                                        <Box mt={0.5}>
                                            <Chip size="small" label={member.isKYCVerified ? "Yes" : "No"}
                                                style={{
                                                    backgroundColor: member.isKYCVerified ? '#2e7d32' : '#546e7a',
                                                    color: '#fff'
                                                }} />
                                        </Box>
                                    </Grid>
                                    {fact("Member KYC reference", member.kycId)}
                                    {fact("Member KYC number", member.kycNo)}
                                </Grid>
                            )}

                            {section("Verification",
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography className={classes.factLabel}>Status</Typography>
                                        <Box mt={0.5}>{statusChip(application.status)}</Box>
                                    </Grid>
                                    {fact("Provider", application.provider)}
                                    {fact("Method", application.method)}
                                    {fact("Provider reference", application.providerRef,
                                        copyIcon(application.providerRef))}
                                    {fact("Provider event", application.providerEventType)}
                                    {fact("Provider event ID", application.providerEventId,
                                        copyIcon(application.providerEventId))}
                                    {fact("Verified country", application.country)}
                                    {fact("Risk score", application.riskScore)}
                                    {fact("Capture link", application.sessionUrl ? "Session link" : undefined,
                                        copyIcon(application.sessionUrl))}
                                    {fact("Link expires", application.expiresAt && formatDate(application.expiresAt))}
                                    {fact("Offline evidence", application.kycPath)}
                                </Grid>
                            )}

                            {(hasValue(application.documentType) || hasValue(application.documentNumber)
                                || hasValue(application.documentRead)) &&
                                section("Document",
                                    <>
                                        <Grid container spacing={1}>
                                            {fact("Type", application.documentType)}
                                            {fact("Number", application.documentNumber,
                                                copyIcon(application.documentNumber))}
                                        </Grid>
                                        {hasValue(application.documentRead) &&
                                            <Box mt={1}>
                                                <FactList value={application.documentRead} classes={classes} />
                                            </Box>}
                                    </>
                                )}

                            {/* The DigiLocker record and the signature verdicts around it. Identity
                                data, and it goes no further than this reviewer's screen. */}
                            {hasIdentityRecord &&
                                section("Identity record",
                                    <>
                                        {hasValue(application.aadhaar) &&
                                            <FactList value={application.aadhaar} classes={classes} />}
                                        {hasValue(application.aadhaarQr) &&
                                            <Box mt={1.5}>
                                                <Typography className={classes.factLabel}>Aadhaar secure QR</Typography>
                                                <FactList value={application.aadhaarQr} classes={classes} />
                                            </Box>}
                                        {hasValue(application.aadhaarXml) &&
                                            <Box mt={1.5}>
                                                <Typography className={classes.factLabel}>Aadhaar offline XML</Typography>
                                                <FactList value={application.aadhaarXml} classes={classes} />
                                            </Box>}
                                    </>
                                )}

                            {(hasValue(application.reasons) || hasValue(application.reviewNote)
                                || hasValue(application.reviewedBy) || hasValue(application.reviewedAt)) &&
                                section("Decision",
                                    <>
                                        <Grid container spacing={1}>
                                            {fact("Decided by", application.reviewedBy)}
                                            {fact("Decided at", application.reviewedAt && formatDate(application.reviewedAt))}
                                        </Grid>
                                        {hasValue(application.reasons) &&
                                            <Box mt={1}>
                                                <Typography className={classes.factLabel}>Reason codes</Typography>
                                                <ul className='stripe-ul'>
                                                    {application.reasons.map((reason, index) => (
                                                        <li key={index}><span>{reason}</span></li>
                                                    ))}
                                                </ul>
                                            </Box>}
                                        {hasValue(application.reviewNote) &&
                                            <Box mt={1}>
                                                <Typography className={classes.factLabel}>Note</Typography>
                                                <Typography variant="body2">{application.reviewNote}</Typography>
                                            </Box>}
                                    </>
                                )}

                            {hasValue(application.artifacts) &&
                                section(
                                    <>
                                        <PhotoLibrary fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                                        Captured images ({application.artifacts.length})
                                    </>,
                                    <Grid container spacing={2}>
                                        {application.artifacts.map((artifact) => (
                                            <ArtifactTile
                                                key={artifact.id}
                                                applicationId={application._id}
                                                artifact={artifact}
                                                classes={classes}
                                                onPreview={setPreview}
                                            />
                                        ))}
                                    </Grid>
                                )}

                            {section("Record",
                                <Grid container spacing={1}>
                                    {fact("Created", application.createdAt && formatDate(application.createdAt))}
                                    {fact("Created by", application.createdBy)}
                                    {fact("Updated", application.updatedAt && formatDate(application.updatedAt))}
                                    {fact("Updated by", application.updatedBy)}
                                </Grid>
                            )}

                            {/* The provider's response as stored — every check, reason code and signal
                                behind the outcome, so a decision can be audited without a callback. */}
                            {hasValue(application.providerPayload) &&
                                section(
                                    "Provider response",
                                    <>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                            <Typography variant="caption" color="textSecondary">
                                                Stored verbatim — the fields above are extracted from it.
                                            </Typography>
                                            <Box>
                                                <Button size="small" startIcon={<FileCopy />}
                                                    onClick={() => copy(JSON.stringify(application.providerPayload, null, 2))}>
                                                    Copy JSON
                                                </Button>
                                                <Button size="small" endIcon={payloadOpen ? <ExpandLess /> : <ExpandMore />}
                                                    onClick={() => setPayloadOpen(!payloadOpen)}>
                                                    {payloadOpen ? "Hide" : "Show"}
                                                </Button>
                                            </Box>
                                        </Box>
                                        <Collapse in={payloadOpen} unmountOnExit>
                                            <pre className={classes.payload}>
                                                {JSON.stringify(application.providerPayload, null, 2)}
                                            </pre>
                                        </Collapse>
                                    </>
                                )}
                        </>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => closeDetail()}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Full size view of one capture. It renders the tile's own object URL, so it lives
                inside the detail dialog — closing that releases the image with the tile. */}
            <Dialog maxWidth="md" fullWidth open={!!preview} onClose={() => setPreview(null)}>
                <DialogTitle>
                    {preview && preview.label}
                    <Close className='mt-1 float-right' style={{ cursor: 'pointer' }} onClick={() => setPreview(null)} />
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {preview && <img src={preview.src} alt={preview.label} className={classes.preview} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreview(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <div className={classes.root}>
                <MUIDataTable
                    title={"KYC Applications"}
                    data={datum}
                    columns={columns}
                    options={options}
                />
            </div>
        </div>
    );
}
